// @ts-strict-ignore
import { EventDispatcher } from 'three';
import { NumberUtils } from '@shared/util/number-utils';
import { Dictionary } from 'typescript-collections';
import { ArticleVO } from '@article/model/vo/article.vo';
import { ArticleManager } from '@article/manager/article.manager';
import { ArrayUtils } from '@shared/util/array-utils';
import { Injectable } from '@angular/core';
import { ArticleDataService } from '@article/service/article-data.service';
import { map } from 'rxjs/operators';
import { of } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ArticleTypeManager extends EventDispatcher {

  private _articleIdToArticleTypeIdDictionary: Dictionary<number, number> = new Dictionary<number, number>();

  constructor(private articleManager: ArticleManager,
              private articleDataService: ArticleDataService) {
    super();
  }

  public getDistinctArticleTypeIdsByArticleIds(articleIds: number[]): number[] {
    if (!articleIds) {
      return null;
    }
    if (articleIds.length === 0) {
      return [];
    }
    const result: number[] = [];
    for (const articleId of articleIds) {
      const articleTypeId: number = this.getArticleTypeIdByArticleId(articleId);
      if (result.indexOf(articleTypeId) === -1) {
        result.push(articleTypeId);
      }
    }
    return result;
  }

  public retrieveArticleTypeIdsByArticleIds(articleIds: number[]) {
    const articleIdsToRetrieve: number[] = [];
    const existingArticleTypeIds: number[] = [];
    if (articleIds) {
      for (let i: number = 0; i < articleIds.length; i++) {
        const articleId: number = articleIds[i];
        if (!this.existsArticleTypeIdByArticleId(articleId)) {
          ArrayUtils.mergeArrays(articleIdsToRetrieve, [articleId]);
        } else {
          existingArticleTypeIds.push(this.getArticleTypeIdByArticleId(articleId));
        }
      }
    }
    if (articleIdsToRetrieve.length > 0) {
      return this.articleDataService
        .findTypeIdsByArticleIds(articleIdsToRetrieve)
        .pipe(map((typeIds) =>
          this.getTypeIdsByArticleIdsSuccessHandler(articleIds, typeIds)));
    } else {
      return of(this.getTypeIdsByArticleIdsSuccessHandler(existingArticleTypeIds, undefined));
    }
  }

  private getArticleTypeIdByArticleId(articleId: number): number {
    if (NumberUtils.isNotNumber(articleId)) {
      return NaN;
    }
    const articleTypeId: number = this._articleIdToArticleTypeIdDictionary.getValue(articleId);
    if (articleTypeId) {
      return articleTypeId;
    }
    const article: ArticleVO = this.articleManager.getCachedArticle(articleId);
    if (article) {
      if (!article.type) {
        return NaN;
      }
      this._articleIdToArticleTypeIdDictionary.setValue(articleId, article.type.id);
      return article.type.id;
    }
    return NaN;
  }

  private existsArticleTypeIdByArticleId(articleId: number): boolean {
    if (NumberUtils.isNotNumber(articleId)) {
      return true;
    }
    const articleTypeId: any = this._articleIdToArticleTypeIdDictionary.getValue(articleId);
    if (articleTypeId) {
      return true;
    }
    const article: ArticleVO = this.articleManager.getCachedArticle(articleId);
    if (article) {
      if (!article.type) {
        return false;
      }
      return true;
    }
    return false;
  }

  public getTypeIdsByArticleIdsSuccessHandler(articleIds: number[], typeIds: number[]) {
    if (ArrayUtils.isNotEmpty(typeIds)) {
      for (let i: number = 0; i < typeIds.length; i++) {
        const articleId: number = articleIds[i];
        const articleTypeId: number = typeIds[i];
        this.addArticleTypeIdForArticleId(articleId, articleTypeId);
      }
    }
    return this.getDistinctArticleTypeIdsByArticleIds(articleIds);
  }

  public addArticleTypeIdForArticleId(articleId: number, articleTypeId: number): void {
    this._articleIdToArticleTypeIdDictionary.setValue(articleId, articleTypeId);
  }
}
